import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { Archive, CheckCircle2, ChevronDown, Cloud, Download, FolderInput, HardDrive, KeyRound, Menu, MoreVertical, Pencil, Plus, Trash2, Upload, X } from "lucide-react";
import type { AgentActivity, ChatMessage, ReasoningEffort, WorkspaceScope } from "./types";
import { ChatPane } from "./components/ChatPane";
import { EditorPane } from "./components/EditorPane";
import { FileTree } from "./components/FileTree";
import { KeyDialog, PromptDialog } from "./components/Dialogs";
import { Sidebar } from "./components/Sidebar";
import { createChat, createProject, db, deleteChat, deleteProject, ensureChat, ensureFirstProject, putTextFile } from "./lib/db";
import { AgentRunError, runAgent } from "./lib/agent";
import { executeTool, undoChanges } from "./lib/tools";
import { exportTaskZip, importFolder, importZip } from "./lib/zip";
import { normalizeProjectPath } from "./lib/path";
import { scopeToProject, scopeToTask } from "./lib/projectScope";
import { DEFAULT_MODEL, DEFAULT_REASONING_EFFORT, getModelDefinition, modelSupportsEffort } from "../shared/models";

type PromptMode = "project" | "file" | "rename-file" | "rename-project" | null;

export default function App() {
  const projects = useLiveQuery(() => db.projects.orderBy("updatedAt").reverse().toArray(), []) ?? [];
  const [projectId, setProjectId] = useState<string | null>(null);
  const queriedChats = useLiveQuery(() => projectId ? db.chats.where("projectId").equals(projectId).reverse().sortBy("updatedAt") : [], [projectId]);
  const chats = useMemo(() => scopeToProject(queriedChats, projectId), [queriedChats, projectId]);
  const [chatId, setChatId] = useState<string | null>(null);
  const queriedChat = useLiveQuery(() => chatId ? db.chats.get(chatId) : undefined, [chatId]);
  const chat = queriedChat?.id === chatId && queriedChat.projectId === projectId ? queriedChat : undefined;
  const workspace = useMemo<WorkspaceScope | null>(() => projectId && chatId ? { projectId, chatId } : null, [chatId, projectId]);
  const queriedFiles = useLiveQuery(() => chatId ? db.taskFiles.where("chatId").equals(chatId).sortBy("path") : [], [chatId]);
  const files = useMemo(() => scopeToTask(queriedFiles, projectId, chatId), [queriedFiles, projectId, chatId]);
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  const selectedFile = files.find((file) => file.path === selectedPath);
  const previewVersion = useMemo(() => {
    const revisionTotal = files.reduce((total, file) => total + file.revision, 0);
    const latestUpdate = files.reduce((latest, file) => Math.max(latest, file.updatedAt), 0);
    return `${files.length}-${revisionTotal}-${latestUpdate}`;
  }, [files]);
  const project = projects.find((item) => item.id === projectId);
  const [apiKey, setApiKey] = useState("");
  const [serverKeyConfigured, setServerKeyConfigured] = useState<boolean | null>(null);
  const [keyOpen, setKeyOpen] = useState(false);
  const [promptMode, setPromptMode] = useState<PromptMode>(null);
  const [writable, setWritable] = useState(true);
  const [running, setRunning] = useState(false);
  const [liveMessage, setLiveMessage] = useState<ChatMessage | null>(null);
  const editorDirtyRef = useRef(false);
  const [toast, setToast] = useState<string | null>(null);
  const [mobileSidebar, setMobileSidebar] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const zipInput = useRef<HTMLInputElement>(null);
  const folderInput = useRef<HTMLInputElement>(null);
  const handleEditorDirty = useCallback((dirty: boolean) => { editorDirtyRef.current = dirty; }, []);

  useEffect(() => { void ensureFirstProject().then((first) => setProjectId((current) => current ?? first.id)); }, []);
  useEffect(() => {
    let active = true;
    void fetch("/api/config", { cache: "no-store" })
      .then(async (response) => response.ok ? response.json() as Promise<{ serverKeyConfigured?: boolean }> : { serverKeyConfigured: false })
      .then((config) => { if (active) setServerKeyConfigured(config.serverKeyConfigured === true); })
      .catch(() => { if (active) setServerKeyConfigured(false); });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    if (serverKeyConfigured) {
      setApiKey("");
      setKeyOpen(false);
    }
  }, [serverKeyConfigured]);
  useEffect(() => {
    if (!projectId) {
      setChatId(null);
      setSelectedPath(null);
      return;
    }
    let active = true;
    setChatId(null);
    setSelectedPath(null);
    void ensureChat(projectId).then((session) => {
      if (active) setChatId(session.id);
    });
    return () => { active = false; };
  }, [projectId]);
  useEffect(() => {
    if (!files.length) setSelectedPath(null);
    else if (!selectedPath || !files.some((file) => file.path === selectedPath)) setSelectedPath(files[0].path);
  }, [files, selectedPath]);
  useEffect(() => setSelectedPath(null), [chatId]);
  useEffect(() => setPreviewMode(false), [chatId, projectId, selectedPath]);
  useEffect(() => { if (!toast) return; const timer = window.setTimeout(() => setToast(null), 3500); return () => window.clearTimeout(timer); }, [toast]);

  const displayMessages = useMemo(() => {
    const saved = chat?.messages ?? [];
    if (!liveMessage || liveMessage.id === undefined || !saved.some((message) => message.id === liveMessage.id)) return saved;
    return saved.map((message) => message.id === liveMessage.id ? liveMessage : message);
  }, [chat?.messages, liveMessage]);

  async function chooseProject(id: string) { if (running) return; setProjectId(id); setMobileSidebar(false); }
  async function newChat() { if (!projectId || running) return; const created = await createChat(projectId); setChatId(created.id); setMobileSidebar(false); }
  async function removeChat(id: string) {
    if (running || !projectId || !window.confirm("Supprimer cette tâche et tous ses fichiers ?")) return;
    await deleteChat({ projectId, chatId: id });
    if (chatId === id) {
      const remaining = await db.chats.where("projectId").equals(projectId).sortBy("updatedAt");
      setChatId(remaining.at(-1)?.id ?? (await createChat(projectId)).id);
    }
  }

  async function saveEditor(path: string, content: string, expectedRevision: number): Promise<number> {
    if (!workspace) throw new Error("Tâche introuvable.");
    const saved = await putTextFile(workspace, path, content, expectedRevision);
    return saved.revision;
  }
  async function deleteFile(path: string) {
    if (!workspace) return;
    const file = files.find((item) => item.path === path);
    if (!file || !window.confirm(`Supprimer « ${path} » ?`)) return;
    await executeTool(workspace, "delete_file", { path, expected_revision: file.revision });
    setToast("Fichier supprimé");
  }

  async function handlePrompt(value: string) {
    if (promptMode === "project") { const created = await createProject(value); setProjectId(created.id); }
    if (promptMode === "file" && workspace) { const path = normalizeProjectPath(value); await putTextFile(workspace, path, "", null); setSelectedPath(path); }
    if (promptMode === "rename-project" && projectId) await db.projects.update(projectId, { name: value, updatedAt: Date.now() });
    if (promptMode === "rename-file" && workspace && selectedFile) {
      const to = normalizeProjectPath(value);
      await executeTool(workspace, "move_file", { from: selectedFile.path, to, expected_revision: selectedFile.revision });
      setSelectedPath(to);
    }
    setPromptMode(null);
  }

  async function removeProject() {
    if (!projectId || !project || !window.confirm(`Supprimer le projet « ${project.name} » et tous ses fichiers locaux ?`)) return;
    let next = projects.find((item) => item.id !== projectId);
    if (!next) next = await createProject("Mon projet");
    await deleteProject(projectId);
    setProjectId(next.id);
  }

  async function importArchive(file?: File) {
    if (!file) return;
    try { const imported = await importZip(file); setProjectId(imported.id); setToast("Projet ZIP importé"); }
    catch (error) { setToast(error instanceof Error ? error.message : "Import impossible"); }
    finally { if (zipInput.current) zipInput.current.value = ""; }
  }
  async function importDirectory(list?: FileList | null) {
    if (!list?.length) return;
    try { const imported = await importFolder(list); setProjectId(imported.id); setToast("Dossier importé"); }
    catch (error) { setToast(error instanceof Error ? error.message : "Import impossible"); }
    finally { if (folderInput.current) folderInput.current.value = ""; }
  }

  async function sendMessage(text: string) {
    if (!chat || !project || running) return;
    if (serverKeyConfigured === null) { setToast("Vérification de la configuration OpenAI…"); return; }
    if (!serverKeyConfigured && !apiKey) { setKeyOpen(true); setToast("Ajoute d’abord ta clé API OpenAI"); return; }
    if (editorDirtyRef.current) {
      const deadline = Date.now() + 2_500;
      while (editorDirtyRef.current && Date.now() < deadline) await new Promise((resolve) => window.setTimeout(resolve, 50));
      if (editorDirtyRef.current) { setToast("Le fichier ouvert n’a pas pu être enregistré."); return; }
    }
    const userMessage: ChatMessage = { id: crypto.randomUUID(), role: "user", content: text, createdAt: Date.now() };
    const assistantMessage: ChatMessage = { id: crypto.randomUUID(), role: "assistant", content: "", createdAt: Date.now(), activities: [] };
    const baseMessages = [...chat.messages, userMessage, assistantMessage];
    await db.chats.update(chat.id, { messages: baseMessages, title: chat.messages.length ? chat.title : text.slice(0, 52), updatedAt: Date.now() });
    let live: ChatMessage = assistantMessage;
    let activities: AgentActivity[] = [];
    setLiveMessage(live);
    setRunning(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const refresh = (patch: Partial<ChatMessage>) => { live = { ...live, ...patch }; setLiveMessage({ ...live }); };
    try {
      const result = await runAgent({ apiKey: serverKeyConfigured ? undefined : apiKey, project, chat, userText: text, writable, signal: controller.signal, callbacks: {
        onTextDelta: (delta) => refresh({ content: live.content + delta }),
        onActivity: (activity) => { activities = [...activities, activity]; refresh({ activities }); },
        onActivityUpdate: (id, patch) => { activities = activities.map((activity) => activity.id === id ? { ...activity, ...patch } : activity); refresh({ activities }); },
      } });
      live = { ...live, content: result.text || live.content, activities, changes: result.changes, web: result.web };
      await db.chats.update(chat.id, { messages: [...chat.messages, userMessage, live], apiInput: result.apiInput, updatedAt: Date.now() });
    } catch (error) {
      const runError = error instanceof AgentRunError ? error : null;
      const stopped = runError?.aborted ?? (error instanceof DOMException && error.name === "AbortError");
      live = { ...live, content: stopped ? "Exécution arrêtée." : `Erreur : ${error instanceof Error ? error.message : "la requête a échoué."}`,
        activities, changes: runError?.changes, web: runError?.web, error: !stopped };
      await db.chats.update(chat.id, { messages: [...chat.messages, userMessage, live],
        ...(runError ? { apiInput: runError.apiInput } : {}), updatedAt: Date.now() });
    } finally {
      abortRef.current = null;
      setRunning(false);
      setLiveMessage(null);
    }
  }

  async function updateChatModel(model: string) {
    if (!chat) return;
    const definition = getModelDefinition(model);
    if (!definition) return;
    const effort = modelSupportsEffort(model, chat.effort) ? chat.effort : definition.fallbackEffort;
    await db.chats.update(chat.id, { model, effort, updatedAt: Date.now() });
  }
  async function updateChatEffort(effort: ReasoningEffort) {
    if (!chat || !modelSupportsEffort(chat.model, effort)) return;
    await db.chats.update(chat.id, { effort, updatedAt: Date.now() });
  }
  async function undoMessage(message: ChatMessage) {
    if (!workspace || !chat || !message.changes?.length || !window.confirm("Annuler tous les changements de cette réponse ?")) return;
    try {
      await undoChanges(workspace, message.changes);
      await db.chats.update(chat.id, { messages: chat.messages.map((item) => item.id === message.id ? { ...item, changes: undefined, content: `${item.content}\n\n_Changements annulés localement._` } : item), updatedAt: Date.now() });
      setToast("Changements annulés");
    } catch (error) {
      setToast(error instanceof Error ? error.message : "Annulation impossible");
    }
  }

  return (
    <div className="app-shell">
      <div className={`mobile-scrim ${mobileSidebar ? "show" : ""}`} onClick={() => setMobileSidebar(false)} />
      <div className={mobileSidebar ? "sidebar-mobile open" : "sidebar-mobile"}><Sidebar projects={projects} chats={chats} projectId={projectId} chatId={chatId} onSelectProject={chooseProject} onSelectChat={(id) => { if (!running) setChatId(id); setMobileSidebar(false); }} onNewChat={newChat} onNewProject={() => setPromptMode("project")} onDeleteChat={removeChat} /></div>
      <div className="desktop-sidebar"><Sidebar projects={projects} chats={chats} projectId={projectId} chatId={chatId} onSelectProject={chooseProject} onSelectChat={(id) => { if (!running) setChatId(id); }} onNewChat={newChat} onNewProject={() => setPromptMode("project")} onDeleteChat={removeChat} /></div>
      <main className="main-area">
        <header className="topbar">
          <button className="icon-button mobile-menu" onClick={() => setMobileSidebar(true)}><Menu size={18} /></button>
          <button className="project-title" onClick={() => setPromptMode("rename-project")}><span>{project?.name ?? "Chargement…"}</span><Pencil size={12} /></button>
          <span className="local-badge"><HardDrive size={12} /> Navigateur</span>
          <div className="topbar-spacer" />
          <input ref={zipInput} hidden type="file" accept=".zip,application/zip" onChange={(event) => void importArchive(event.target.files?.[0])} />
          <input ref={folderInput} hidden type="file" multiple onChange={(event) => void importDirectory(event.target.files)} {...({ webkitdirectory: "" } as Record<string, string>)} />
          <button className="toolbar-button" onClick={() => folderInput.current?.click()} title="Importer un dossier"><FolderInput size={15} /><span>Dossier</span></button>
          <button className="toolbar-button" onClick={() => zipInput.current?.click()} title="Importer un ZIP"><Upload size={15} /><span>ZIP</span></button>
          <button className="toolbar-button primary-subtle" onClick={() => workspace && void exportTaskZip(workspace)}><Download size={15} /><span>Exporter</span></button>
          {serverKeyConfigured
            ? <span className="icon-button key-active" title="Clé serveur Cloudflare active"><Cloud size={16} /></span>
            : <button className={`icon-button ${apiKey ? "key-active" : ""}`} onClick={() => setKeyOpen(true)} title="Clé API utilisateur"><KeyRound size={16} /></button>}
          <button className="icon-button danger-hover" onClick={() => void removeProject()} title="Supprimer le projet"><Trash2 size={15} /></button>
        </header>
        <div className="workspace-grid">
          <ChatPane messages={displayMessages} model={chat?.model ?? DEFAULT_MODEL} effort={chat?.effort ?? DEFAULT_REASONING_EFFORT} writable={writable} keyMode={serverKeyConfigured ? "server" : apiKey ? "user" : serverKeyConfigured === null ? "loading" : "missing"} running={running} onSend={(text) => void sendMessage(text)} onStop={() => abortRef.current?.abort()} onModelChange={(model) => void updateChatModel(model)} onEffortChange={(effort) => void updateChatEffort(effort)} onWritableChange={setWritable} onOpenKey={() => setKeyOpen(true)} onUndo={(message) => void undoMessage(message)} />
          <section className="code-workspace"><FileTree key={`files:${chatId ?? "none"}`} files={files} selectedPath={selectedPath} onSelect={setSelectedPath} onNewFile={() => setPromptMode("file")} onDelete={(path) => void deleteFile(path)} footer={<><Archive size={12} /><span>{files.length} fichier{files.length > 1 ? "s" : ""}</span></>} /><EditorPane key={`editor:${chatId ?? "none"}`} file={selectedFile} files={files} readOnly={running} previewMode={previewMode} previewVersion={previewVersion} onPreviewModeChange={setPreviewMode} onSave={saveEditor} onDirtyChange={handleEditorDirty} onRename={() => selectedFile && setPromptMode("rename-file")} /></section>
        </div>
      </main>
      <KeyDialog open={keyOpen && serverKeyConfigured !== true} hasKey={!!apiKey} onSave={(key) => { setApiKey(key); setKeyOpen(false); setToast("Clé chargée en mémoire"); }} onClear={() => { setApiKey(""); setKeyOpen(false); setToast("Clé retirée"); }} onClose={() => setKeyOpen(false)} />
      <PromptDialog open={promptMode !== null} title={promptMode === "project" ? "Nouveau projet" : promptMode === "file" ? "Nouveau fichier" : promptMode === "rename-file" ? "Renommer le fichier" : "Renommer le projet"} label={promptMode === "file" || promptMode === "rename-file" ? "Chemin relatif" : "Nom du projet"} initialValue={promptMode === "rename-file" ? selectedFile?.path : promptMode === "rename-project" ? project?.name : ""} placeholder={promptMode === "file" ? "src/index.ts" : "Mon projet"} onClose={() => setPromptMode(null)} onSubmit={handlePrompt} />
      {toast && <div className="toast"><CheckCircle2 size={15} />{toast}<button onClick={() => setToast(null)}><X size={13} /></button></div>}
    </div>
  );
}
